import { useState } from "react";
import { Modal, Form, Row, Col, Button } from "react-bootstrap";

import { useIsMounted, useLambdaApi } from "hooks";
import { ButtonWithSpinner, ErrorMessages } from "components/common";
import { roundToTwo } from "utils";

export default function ModifyInstance({
  instance,
  instanceTypes,
  getResources,
  close,
}) {
  const api = useLambdaApi();
  const isMounted = useIsMounted();
  const [isLoading, setIsLoading] = useState(false);
  const [instanceType, setInstanceType] = useState(instance.instance_type);
  const [errors, setErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();

    setIsLoading(true);

    api
      .execute("ec2.instance.modify", {
        instance_id: instance.instance_id,
        instance_type: instanceType,
      })
      .then(close)
      .catch((errors) => setErrors(errors))
      .finally(() => {
        getResources();
        isMounted.current && setIsLoading(false);
      });
  };

  const getCost = () => {
    const zeroCost = roundToTwo(0);
    const cost = { current: zeroCost, new: zeroCost };

    if (!instanceTypes.length) {
      return cost;
    }

    const getInstance = (typeToFind) =>
      instanceTypes.find((type) => type.instance_type === typeToFind);
    cost.current = roundToTwo(getInstance(instance.instance_type).cost);
    cost.new = roundToTwo(getInstance(instanceType).cost);

    return cost;
  };

  const cost = getCost();

  return (
    <Modal
      show
      animation={false}
      onHide={close}
      size="lg"
      className="launch-instance-modal"
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title>Modify Instance</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group as={Row} className="mb-3 form-group-plaintext">
            <Form.Label column sm="3">
              Instance
            </Form.Label>
            <Col sm="9">
              <Form.Control plaintext readOnly defaultValue={instance.name} />
            </Col>
          </Form.Group>

          {/* INSTANCE TYPE */}
          <Form.Group as={Row}>
            <Form.Label column sm="3">
              Instance Type
            </Form.Label>
            <Col sm="8">
              <Form.Control
                as="select"
                name="instanceType"
                custom
                value={instanceType}
                onChange={(e) => {
                  setInstanceType(e.target.value);
                  setErrors({});
                }}
                required
                isInvalid={!!errors.instance_type}
              >
                {instanceTypes.map((instanceType) => (
                  <option
                    key={instanceType.instance_type}
                    value={instanceType.instance_type}
                  >
                    {instanceType.instance_type} ({instanceType.vcpus} vCPUs,{" "}
                    {instanceType.memory}GiB, ${instanceType.cost})
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {errors.instance_type}
              </Form.Control.Feedback>
            </Col>
          </Form.Group>
          {/* COST */}
          <Row className="justify-content-end mt-5">
            <Col xs="auto text-right">
              <table>
                <tbody className="font-weight-bold">
                  <tr>
                    <td className="pr-3">Current cost (while running):</td>
                    <td>${cost.current}</td>
                  </tr>
                  <tr>
                    <td className="pr-3">New cost (while running):</td>
                    <td>${cost.new}</td>
                  </tr>
                </tbody>
              </table>
            </Col>
          </Row>

          {/* GENERIC ERRORS */}
          <ErrorMessages
            errors={errors}
            keysToIgnore={["instance_type"]}
            className="mt-3"
          ></ErrorMessages>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={close} disabled={isLoading}>
            Cancel
          </Button>
          <ButtonWithSpinner type="submit" isLoading={isLoading}>
            Modify
          </ButtonWithSpinner>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
